import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const WORK_PERMIT_TASK_ID = 40;
const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

const validateFile = (file) => {
  if (!file) return null;

  if (!allowedTypes.includes(file.type)) {
    alert("Only PDF, JPG, or PNG files are allowed.");
    return null;
  }

  return file;
};

const WorkPermitForm = () => {
  const navigate = useNavigate();
  const { id: paramId, wpId } = useParams();
  const isNew = !wpId || wpId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [expiryDate, setExpiryDate] = useState("");
  const [file, setFile] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [createdDate, setCreatedDate] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("not_completed");

  const fetchTaskStatus = useCallback(async () => {
    if (!userId || !wpId || wpId === "new") return;

    try {
      const res = await axios.get(`${API_BASE}/api/usertasks/status/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const refKey = `${WORK_PERMIT_TASK_ID}_${wpId}`;
      const status =
        res.data.status?.[refKey] ??
        res.data.status?.work_permit ??
        "not_completed";

      setCurrentStatus(status);
    } catch (err) {
      console.error("Failed to fetch work permit status", err);
    }
  }, [userId, wpId]);

  useEffect(() => {
    if (!isNew) {
      const fetchPermit = async () => {
        try {
          const res = await axios.get(
            `${API_BASE}/api/workpermit/detail/${wpId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data = res.data;
          setExpiryDate(
            data.wp_expiry_date ? data.wp_expiry_date.split("T")[0] : ""
          );
          setUploadedFileName(data.wp_file);
          setOriginalFile(data.wp_original);
          setCreatedDate(data.wp_created_date);
        } catch (err) {
          console.error("Fetch failed", err);
        }
      };
      fetchPermit();
    }
  }, [isNew, wpId]);

  useEffect(() => {
    fetchTaskStatus();
  }, [fetchTaskStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file && isNew) return alert("Please upload a PDF, JPG, or PNG file!");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("expiry_date", expiryDate);
    formData.append("userId", userId);

    try {
      if (isNew) {
        const res = await axios.post(
          `${API_BASE}/api/workpermit?userId=${userId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        alert("Work permit created successfully!");
        const createdId = res.data?.id;
        if (createdId) {
          navigate(`/workpermit/${userId}/detail/${createdId}`);
          return;
        }
      } else {
        await axios.put(
          `${API_BASE}/api/workpermit/${wpId}?userId=${userId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        alert("Work permit updated successfully!");
      }

      navigate(`/workpermit/${userId}`);
    } catch (err) {
      console.error("Save failed", err);
      alert("Upload failed. Please try again.");
    }
  };

  const isLocked = !isAdmin && currentStatus === "completed";

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Work Permit</span>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/workpermit/${userId}`)}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Work Permit</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit} className="mt-4">
          <div className="col-md-3 mb-3">
            <label>Expiry Date</label>
            <input
              type="date"
              className="form-control"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              disabled={isLocked}
            />
          </div>

          <div className="mb-3">
            <label>Select File (PDF / JPG / PNG) *</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={isLocked}
              onChange={(e) => {
                const selected = validateFile(e.target.files[0]);
                setFile(selected);
              }}
            />
          </div>

          {originalFile && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded File:</div>
              <a
                href={`${API_BASE}/uploads/workpermits/${uploadedFileName}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: "bold", textDecoration: "underline" }}
              >
                {originalFile}
              </a>
              {createdDate && (
                <div className="text-muted mt-1">
                  Uploaded: {new Date(createdDate).toLocaleString()}
                </div>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary mt-3"
              disabled={isLocked}
            >
              Save
            </button>
          </div>
        </form>

        {!isNew && (
          <>
            <hr className="my-4" />
            {currentStatus === "not_completed" && (
              <SignatureManager
                taskName="work_permit"
                taskId={WORK_PERMIT_TASK_ID}
                referenceId={wpId}
                userId={userId}
                isAdmin={isAdmin}
              />
            )}

            <ReviewManager
              taskName="work_permit"
              taskId={WORK_PERMIT_TASK_ID}
              referenceId={wpId}
              userId={userId}
              isAdmin={isAdmin}
              onReviewed={fetchTaskStatus}
            />
          </>
        )}
      </div>
    </>
  );
};

export default WorkPermitForm;
